import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, MenuProps, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useState } from 'react'

import {
  useClassifierServiceDeleteDeviceTypeClassifierElementEndpoint,
  useClassifierServiceGetDeviceTypeClassifierElementListEndpoint,
  useClassifierServiceGetDeviceTypeClassifierElementListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  CreateDeviceTypeClassifierElementResponse
} from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  TableActionButtons
} from 'src/components'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { DeviceTypeModal } from '../components'

export const DeviceTypesPage = () => {
  const { t, currentLang } = useLocales()

  const queryClient = useQueryClient()
  const pagination = useTablePagination()

  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const classifierId = useClassifierId(
    ClassifierType.DEVICE_TYPE,
    ClassifierCategory.INSTITUTION
  )

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),

    classifierId: String(classifierId)
  }

  const { data, isLoading } =
    useClassifierServiceGetDeviceTypeClassifierElementListEndpoint(
      queryParams,
      [queryParams],
      {
        enabled: !!classifierId
      }
    )

  const { mutate } =
    useClassifierServiceDeleteDeviceTypeClassifierElementEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('equipments.delete_success_device'))
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetDeviceTypeClassifierElementListEndpointKey
          ]
        })
      }
    })

  const onToggle = () => {
    setIsOpen((prev) => !prev)
  }

  const onCloseModal = () => {
    setCurrentId(undefined)
    onToggle()
  }

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('equipments.delete_device')}
          onConfirm={() =>
            mutate({
              elementId: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnsType<CreateDeviceTypeClassifierElementResponse> = [
    {
      key: 'name',
      title: t('medicine_register.products.name'),
      render: (_, { translation }) => translation?.[currentLang?.value]
    },
    {
      key: 'period',
      title: t('equipments.inspection_period'),
      render: (_, { inspectionPeriodMonths }) =>
        `${inspectionPeriodMonths} ${t('common.months')}`
    },
    {
      key: 'comments',
      title: t('common.comments'),
      render: (_, { comments }) => comments || '-'
    },
    {
      key: 'order',
      title: t('common.order'),
      render: (_, { order }) => order ?? '-'
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.device_types')}
      items={[{ title: t('menu.device_types') }]}
      extra={
        <Button onClick={onToggle} type="primary">
          {t('equipments.add_new_device')}
        </Button>
      }
      extraProps={{ align: 'flex-end' }}
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.data}
        loading={isLoading}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isOpen && (
        <DeviceTypeModal
          id={currentId}
          onCancel={onCloseModal}
          classifierId={classifierId}
        />
      )}
    </PageBreadcrumbs>
  )
}
